import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, Drawer, Field, Input } from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement, useState } from 'react';
import { useForm } from 'react-hook-form';

import { inventoryAdjustFormSchema, type InventoryAdjustFormValues } from '../../products/schemas';
import { useAdjustInventoryMutation } from '../api';

import { useToast } from '@/components/ui/toast/ToastProvider';
import { whenDefined } from '@/lib/optional-props';

export function StockAdjustDrawer({
  productId,
  variantId,
  productName,
  sku,
  onClose,
}: {
  productId: string;
  variantId: string | null;
  productName: string;
  sku: string;
  onClose: () => void;
}): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const adjustMutation = useAdjustInventoryMutation(productId, variantId);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InventoryAdjustFormValues>({
    resolver: zodResolver(inventoryAdjustFormSchema),
  });

  async function onSubmit(values: InventoryAdjustFormValues): Promise<void> {
    setFormError(null);
    try {
      const inventory = await adjustMutation.mutateAsync(values);
      toast.success(
        t('merchant.inventory.adjustedToast', {
          available: inventory.quantityAvailable,
          reserved: inventory.quantityReserved,
        }),
      );
      onClose();
    } catch (error) {
      setFormError(t(resolveErrorMessageKey(error)));
    }
  }

  return (
    <Drawer
      open
      title={t('merchant.inventory.drawer.title', {
        name: productName,
        skuSuffix: variantId !== null ? ` (${sku})` : '',
      })}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('merchant.common.cancel')}
          </Button>
          <Button type="submit" form="stock-adjust-form" loading={adjustMutation.isPending}>
            {t('merchant.inventory.drawer.apply')}
          </Button>
        </>
      }
    >
      <form
        id="stock-adjust-form"
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
        noValidate
        className="flex flex-col gap-4"
      >
        <Field
          label={t('merchant.inventory.drawer.adjustmentLabel')}
          hint={t('merchant.inventory.drawer.adjustmentHint')}
          {...whenDefined('error', errors.delta?.message)}
        >
          {(bind) => (
            <Input
              {...bind}
              type="number"
              step="1"
              {...register('delta', { valueAsNumber: true })}
            />
          )}
        </Field>

        <Field
          label={t('merchant.orders.reasonLabel')}
          optional
          {...whenDefined('error', errors.reason?.message)}
        >
          {(bind) => (
            <Input
              {...bind}
              {...register('reason')}
              placeholder={t('merchant.inventory.drawer.reasonPlaceholder')}
            />
          )}
        </Field>

        <Field
          label={t('merchant.inventory.drawer.thresholdLabel')}
          optional
          hint={t('merchant.inventory.drawer.thresholdHint')}
          {...whenDefined('error', errors.quantityMinThreshold?.message)}
        >
          {(bind) => (
            <Input
              {...bind}
              type="number"
              step="1"
              min="0"
              {...register('quantityMinThreshold', { valueAsNumber: true })}
            />
          )}
        </Field>

        {formError !== null ? (
          <p role="alert" className="rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical">
            {formError}
          </p>
        ) : null}
      </form>
    </Drawer>
  );
}
